import { useState, useEffect } from "react";
import { api, formatCurrency, monthNames } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Download, BarChart3, Users, Filter, FileDown } from "lucide-react";
import { exportToPDF } from "@/lib/pdf";

export const ReportsPage = () => {
  const [sellers, setSellers] = useState([]);
  const [selectedSellers, setSelectedSellers] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState("all");

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2015 + 1 }, (_, i) => currentYear - i);

  useEffect(() => {
    const fetchSellers = async () => {
      try {
        const response = await api.getSellers();
        setSellers(response.data);
      } catch (error) {
        console.error(error);
      }
    };
    fetchSellers();
  }, []);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const params = {
        year: selectedYear,
        ...(selectedMonth && selectedMonth !== "all" && { month: parseInt(selectedMonth) }),
        ...(selectedSellers.length > 0 && { seller_ids: selectedSellers.join(",") })
      };
      const response = await api.getReportBySeller(params);
      setReport(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement du rapport");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth, selectedSellers]);

  const toggleSeller = (sellerId) => {
    setSelectedSellers(prev =>
      prev.includes(sellerId)
        ? prev.filter(id => id !== sellerId)
        : [...prev, sellerId]
    );
  };

  const handleExport = () => {
    if (!report?.report) return;
    
    const headers = ["Vendeur", "Nombre commandes", "CA Total", "En attente", "Terminées"];
    const rows = report.report.map(r => [
      r.seller_name,
      r.order_count,
      r.total_ca,
      r.pending_count,
      r.completed_count
    ]);
    
    const csv = [headers.join(";"), ...rows.map(r => r.join(";"))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `rapport_vendeurs_${selectedYear}${selectedMonth ? "_" + selectedMonth : ""}.csv`;
    link.click();
    
    toast.success("Export réussi");
  };

  const totalCA = report?.report?.reduce((sum, r) => sum + r.total_ca, 0) || 0;
  const totalOrders = report?.report?.reduce((sum, r) => sum + r.order_count, 0) || 0;

  return (
    <div className="space-y-6 animate-in" data-testid="reports-page" id="reports-content">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Rapports</h1>
          <p className="text-muted-foreground mt-1">
            Analysez les performances par vendeur
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => exportToPDF("reports-content", "rapports-chantierflow", "Rapports par vendeur").catch(() => toast.error("Erreur d'export PDF"))}
            variant="outline"
            data-testid="export-report-pdf"
          >
            <FileDown className="h-4 w-4 mr-2" />
            PDF
          </Button>
          <Button onClick={handleExport} variant="outline" data-testid="export-report-btn">
            <Download className="h-4 w-4 mr-2" />
            CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtres
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <div className="w-40">
              <Label className="mb-2 block">Année</Label>
              <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
                <SelectTrigger data-testid="report-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label className="mb-2 block">Mois</Label>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger data-testid="report-month-select">
                  <SelectValue placeholder="Toute l'année" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toute l'année</SelectItem>
                  {monthNames.map((name, idx) => (
                    <SelectItem key={idx} value={String(idx + 1)}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Seller selection */}
          <div>
            <Label className="mb-2 block">Vendeurs</Label>
            <div className="flex flex-wrap gap-4">
              {sellers.map((seller) => (
                <div key={seller.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={seller.id}
                    checked={selectedSellers.includes(seller.id)}
                    onCheckedChange={() => toggleSeller(seller.id)}
                    data-testid={`seller-checkbox-${seller.id}`}
                  />
                  <Label htmlFor={seller.id} className="cursor-pointer">
                    {seller.name}
                  </Label>
                </div>
              ))}
              {sellers.length === 0 && (
                <p className="text-sm text-muted-foreground">Aucun vendeur</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-primary/10 rounded-sm flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">CA Total</p>
                <p className="text-2xl font-heading font-bold currency">{formatCurrency(totalCA)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-secondary/10 rounded-sm flex items-center justify-center">
                <Users className="h-6 w-6 text-secondary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total commandes</p>
                <p className="text-2xl font-heading font-bold">{totalOrders}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Report Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">
            Rapport par vendeur - {selectedMonth ? monthNames[parseInt(selectedMonth) - 1] : "Année"} {selectedYear}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : !report?.report || report.report.length === 0 ? (
            <div className="text-center py-12">
              <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucune donnée pour cette période</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Vendeur</th>
                    <th>Nombre de commandes</th>
                    <th>CA Total</th>
                    <th>En attente</th>
                    <th>Terminées</th>
                    <th>% du total</th>
                  </tr>
                </thead>
                <tbody>
                  {report.report.map((row) => (
                    <tr key={row.seller_id} data-testid={`report-row-${row.seller_id}`}>
                      <td className="font-medium">{row.seller_name}</td>
                      <td>{row.order_count}</td>
                      <td className="currency font-medium">{formatCurrency(row.total_ca)}</td>
                      <td>
                        <span className="text-yellow-600">{row.pending_count}</span>
                      </td>
                      <td>
                        <span className="text-green-600">{row.completed_count}</span>
                      </td>
                      <td>
                        {totalCA > 0 ? ((row.total_ca / totalCA) * 100).toFixed(1) : 0}%
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-muted font-medium">
                    <td>Total</td>
                    <td>{totalOrders}</td>
                    <td className="currency">{formatCurrency(totalCA)}</td>
                    <td>{report.report.reduce((s, r) => s + r.pending_count, 0)}</td>
                    <td>{report.report.reduce((s, r) => s + r.completed_count, 0)}</td>
                    <td>100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsPage;
